import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { Toggle } from 'designSystem/components/Toggle'
import type { LotoPointKey } from 'features/lotoReports/lotoIsolationPoints'
import type {
  LotoReportScope,
  LotoTargetEquipment,
} from 'features/lotoReports/lotoReportSchemas'
import type { VisitLotoPage } from 'features/lotoReports/useLotoDocumentTab'
import { ChevronDown, ChevronUp, MousePointerClick, X } from 'lucide-react'
import { type ReactElement, useEffect, useId, useState } from 'react'

import {
  LotoEquipmentPickHint,
  LotoEquipmentPickSlot,
  LotoModeSwatch,
  LotoPointsHeader,
  LotoSectionTitle,
  LotoTargetHead,
  LotoTargetList,
  LotoTargetRow,
  LotoTargetText,
} from './lotoPagePanel.styles'
import { LotoTargetDrawings } from './LotoTargetDrawings'

type LotoTargetEquipmentListProps = {
  targets: LotoTargetEquipment[]
  picking: boolean
  onPickingChange: (picking: boolean) => void
  onRemove: (key: LotoPointKey) => void
  scope: LotoReportScope
  onVisitPage: VisitLotoPage
}

type TargetRowProps = Pick<
  LotoTargetEquipmentListProps,
  'onRemove' | 'scope' | 'onVisitPage'
> & { target: LotoTargetEquipment }

function TargetRow({
  target,
  onRemove,
  scope,
  onVisitPage,
}: TargetRowProps): ReactElement {
  const { t } = useLingui()
  const drawingsId = useId()
  const [showDrawings, setShowDrawings] = useState(false)
  const name = target.tag || target.description
  return (
    <LotoTargetRow>
      <LotoTargetHead>
        <LotoTargetText>
          <Text as='h4' variant='label'>
            {name}
          </Text>
          {target.tag && target.description && (
            <Text variant='caption' color='muted'>
              {target.description}
            </Text>
          )}
        </LotoTargetText>
        <Button
          variant='ghost'
          size='xs'
          aria-expanded={showDrawings}
          aria-controls={showDrawings ? drawingsId : undefined}
          aria-label={t`Drawings for ${name}`}
          onClick={() => setShowDrawings((shown) => !shown)}>
          <Trans>Drawings</Trans>
          {showDrawings ? <ChevronUp /> : <ChevronDown />}
        </Button>
        <Button
          variant='ghost'
          size='icon-sm'
          aria-label={t`Remove ${name}`}
          onClick={() =>
            onRemove({ pageId: target.page_id, nodeId: target.node_id })
          }>
          <X />
        </Button>
      </LotoTargetHead>
      {showDrawings && (
        <LotoTargetDrawings
          id={drawingsId}
          name={name}
          scope={scope}
          nodeId={target.node_id}
          onVisitPage={onVisitPage}
        />
      )}
    </LotoTargetRow>
  )
}

function useStopPickingOnEscape(
  picking: boolean,
  onPickingChange: (picking: boolean) => void
): void {
  useEffect(() => {
    if (!picking) return undefined
    const stop = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onPickingChange(false)
    }
    document.addEventListener('keydown', stop)
    return () => document.removeEventListener('keydown', stop)
  }, [picking, onPickingChange])
}

export function LotoTargetEquipmentList({
  targets,
  picking,
  onPickingChange,
  onRemove,
  scope,
  onVisitPage,
}: LotoTargetEquipmentListProps): ReactElement {
  const headingId = useId()
  useStopPickingOnEscape(picking, onPickingChange)
  return (
    <Box display='flex' direction='column' gap='xs'>
      <LotoPointsHeader>
        <LotoSectionTitle>
          <LotoModeSwatch $mode='equipment' />
          <Text as='h3' id={headingId} variant='caption' color='muted' truncate>
            <Trans>Target equipment</Trans>
          </Text>
        </LotoSectionTitle>
        <LotoEquipmentPickSlot>
          <Toggle
            variant='outline'
            pressed={picking}
            onPressedChange={onPickingChange}>
            <MousePointerClick />
            <Trans>Select on drawing</Trans>
          </Toggle>
        </LotoEquipmentPickSlot>
      </LotoPointsHeader>
      {picking && (
        <LotoEquipmentPickHint>
          <Text variant='caption' role='status'>
            <Trans>
              Click equipment on the drawing to add or remove it. Press Esc when
              done.
            </Trans>
          </Text>
        </LotoEquipmentPickHint>
      )}
      {targets.length === 0 && !picking && (
        <Text variant='caption' color='muted'>
          <Trans>No equipment targeted yet.</Trans>
        </Text>
      )}
      {targets.length > 0 && (
        <LotoTargetList aria-labelledby={headingId}>
          {targets.map((target) => (
            <TargetRow
              key={target.node_id}
              target={target}
              onRemove={onRemove}
              scope={scope}
              onVisitPage={onVisitPage}
            />
          ))}
        </LotoTargetList>
      )}
    </Box>
  )
}
